<template>
  <div class="card bg-white bg-opacity-90 mb-4">
    <div v-if="illustration" class="card-illustration text-center p-4 bg-light">
      <img
        :src="getIllustrationPath(illustration)"
        :alt="title + ' diagram'"
        class="implement-illustration"
      />
    </div>
    <div class="card-body p-4">
      <h2 class="h5 card-title text-start fw-bold">{{ title }}</h2>
      <p class="card-text text-start">{{ description }}</p>
      <ul v-if="details && details.length" class="text-start">
        <li v-for="(detail, index) in details" :key="index">{{ detail }}</li>
      </ul>
      <!-- SonarQube: "image" variable name triggers false positive about redundant alt text.
           The actual alt values come from JSON data and do not contain the word "image". -->
      <div v-if="images && images.length" class="thumbnail-gallery">
        <button
          v-for="(image, index) in images"
          :key="index"
          type="button"
          class="thumbnail-btn"
          :aria-label="`View ${image.alt}`"
          @click="openImageModal(image, index)"
        >
          <img
            :src="getImagePath(image.src)"
            :alt="image.alt"
            class="thumbnail"
          />
        </button>
      </div>
    </div>
  </div>

  <!-- Image Lightbox Modal -->
  <dialog
    v-if="selectedImage"
    ref="imageModal"
    open
    :aria-label="`Viewing: ${selectedImage.alt}`"
    class="image-modal position-fixed top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center p-4"
    tabindex="-1"
    @click="closeImageModal"
    @keydown.esc="closeImageModal"
    @keydown.tab="trapFocus"
    @keydown.left="previousModalImage"
    @keydown.right="nextModalImage"
  >
    <!-- Previous Button -->
    <button
      v-if="hasPreviousImage"
      ref="prevBtn"
      type="button"
      class="modal-nav-btn btn btn-lg btn-light position-absolute start-0 ms-3 opacity-75 top-50 translate-middle-y"
      aria-label="Previous image"
      @click.stop="previousModalImage"
    >
      <i class="bi bi-chevron-left" aria-hidden="true"></i>
    </button>

    <!-- Next Button -->
    <button
      v-if="hasNextImage"
      ref="nextBtn"
      type="button"
      class="modal-nav-btn btn btn-lg btn-light position-absolute end-0 me-3 opacity-75 top-50 translate-middle-y"
      aria-label="Next image"
      @click.stop="nextModalImage"
    >
      <i class="bi bi-chevron-right" aria-hidden="true"></i>
    </button>

    <div
      class="modal-content-wrapper position-relative d-flex flex-column"
      @click.stop
    >
      <!-- Close Button -->
      <button
        ref="closeBtn"
        type="button"
        class="modal-close-btn btn-close position-absolute top-0 end-0 m-2 p-2 rounded-circle"
        aria-label="Close image viewer"
        @click="closeImageModal"
      ></button>

      <!-- Image -->
      <img
        :src="getImagePath(selectedImage.src)"
        :alt="selectedImage.alt"
        class="modal-image w-100 rounded"
      />

      <!-- Caption -->
      <div class="modal-caption text-center text-white p-3 rounded-bottom">
        <p class="h5 mb-2">{{ selectedImage.alt }}</p>
        <p class="mb-0 small opacity-75">
          {{ currentImageIndex + 1 }} of {{ images.length }}
        </p>
      </div>
    </div>
  </dialog>
</template>

<script>
// Import all images using Vite's glob import
const imageModules = import.meta.glob("../../assets/**/*.{jpg,jpeg,png,webp}", {
  eager: true,
  import: "default"
});

// Import SVG illustrations
const svgModules = import.meta.glob("../../assets/**/*.svg", {
  eager: true,
  import: "default"
});

export default {
  name: "TillageImplement",
  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    details: {
      type: Array,
      default: () => []
    },
    images: {
      type: Array,
      required: true
    },
    illustration: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      selectedImage: null,
      currentImageIndex: 0,
      triggerElement: null // Store element that opened modal for focus restoration
    };
  },
  computed: {
    hasPreviousImage() {
      return this.selectedImage && this.currentImageIndex > 0;
    },
    hasNextImage() {
      return (
        this.selectedImage && this.currentImageIndex < this.images.length - 1
      );
    }
  },
  methods: {
    getIllustrationPath(path) {
      if (!path) {
        return "";
      }

      try {
        const cleanPath = path.replace(/^\//, "");
        const moduleKey = `../../${cleanPath}`;

        if (svgModules[moduleKey]) {
          return svgModules[moduleKey];
        }

        console.error(`SVG illustration not found: ${moduleKey}`);
        return "";
      } catch (error) {
        console.error(`Error loading illustration ${path}:`, error);
        return "";
      }
    },
    getImagePath(path) {
      if (!path) {
        console.error("getImagePath called with undefined/null path");
        return "";
      }

      try {
        // Remove leading slash to get relative path
        // e.g., "/assets/moldboard/image01.jpg" -> "assets/moldboard/image01.jpg"
        const cleanPath = path.replace(/^\//, "");

        // Build the key that matches the glob import pattern
        const moduleKey = `../../${cleanPath}`;

        // Look up the imported image
        if (imageModules[moduleKey]) {
          return imageModules[moduleKey];
        }

        console.error(`Image not found: ${moduleKey}`);
        return "";
      } catch (error) {
        console.error(`Error loading image ${path}:`, error);
        return "";
      }
    },
    openImageModal(image, index) {
      // Store the trigger element for focus restoration when modal closes
      this.triggerElement = document.activeElement;
      this.selectedImage = image;
      this.currentImageIndex = index;
      this.$nextTick(() => {
        if (this.$refs.closeBtn) {
          this.$refs.closeBtn.focus();
        }
      });
    },
    closeImageModal() {
      this.selectedImage = null;
      this.currentImageIndex = 0;
      // Restore focus to the element that triggered the modal
      this.$nextTick(() => {
        if (this.triggerElement) {
          this.triggerElement.focus();
          this.triggerElement = null;
        }
      });
    },
    /**
     * Trap focus within the modal (WCAG 2.4.3 Focus Order)
     * @param {KeyboardEvent} event - The keydown event
     */
    trapFocus(event) {
      // Get all focusable elements within the modal
      const focusableElements = [];
      if (this.$refs.prevBtn) focusableElements.push(this.$refs.prevBtn);
      if (this.$refs.nextBtn) focusableElements.push(this.$refs.nextBtn);
      if (this.$refs.closeBtn) focusableElements.push(this.$refs.closeBtn);

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Shift+Tab on first element -> go to last
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
      // Tab on last element -> go to first
      else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    },
    previousModalImage() {
      if (this.hasPreviousImage) {
        this.currentImageIndex--;
        this.selectedImage = this.images[this.currentImageIndex];
      }
    },
    nextModalImage() {
      if (this.hasNextImage) {
        this.currentImageIndex++;
        this.selectedImage = this.images[this.currentImageIndex];
      }
    }
  }
};
</script>

<style scoped>
.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.card-illustration {
  border-bottom: 1px solid #dee2e6;
}

.implement-illustration {
  max-width: 200px;
  max-height: 180px;
  width: auto;
  height: auto;
}

.card-body {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.thumbnail-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
  padding-top: 1rem;
}

.thumbnail-btn {
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

.thumbnail-btn:focus {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
  border-radius: 4px;
}

.thumbnail {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
  border: 2px solid #ddd;
  display: block;
  transition:
    transform 0.2s,
    border-color 0.2s;
}

.thumbnail-btn:hover .thumbnail {
  transform: scale(1.05);
  border-color: #0d6efd;
}

/* Image Modal Styles */
.image-modal {
  background: rgba(0, 0, 0, 0.9);
  z-index: 1050;
}

.modal-nav-btn {
  z-index: 1051;
}

.image-modal .modal-close-btn {
  z-index: 1051;
  background-color: white;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  opacity: 1;
}

.image-modal .modal-close-btn:hover {
  background-color: #e0e0e0;
}

.modal-content-wrapper {
  max-width: 80vw;
  max-height: 80vh;
}

.modal-image {
  max-height: 80vh;
  object-fit: contain;
}

.modal-caption {
  background: rgba(0, 0, 0, 0.8);
}
</style>
